import React, { useEffect, useRef, useState } from 'react';
import Hangul from 'hangul-js';
import Keyboard from 'react-simple-keyboard';
import { useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// 사전에 명사가 풍부하게 존재하는 검증된 2글자 초성 풀 (120개 이상)
const VERIFIED_INITIALS_POOL = [
  'ㄱㄱ', 'ㄱㄴ', 'ㄱㄷ', 'ㄱㄹ', 'ㄱㅁ', 'ㄱㅂ', 'ㄱㅅ', 'ㄱㅇ', 'ㄱㅈ', 'ㄱㅊ', 'ㄱㅌ', 'ㄱㅍ', 'ㄱㅎ',
  'ㄴㄱ', 'ㄴㄷ', 'ㄴㄹ', 'ㄴㅁ', 'ㄴㅂ', 'ㄴㅅ', 'ㄴㅇ', 'ㄴㅈ', 'ㄴㅊ', 'ㄴㅎ',
  'ㄷㄱ', 'ㄷㄹ', 'ㄷㅁ', 'ㄷㅂ', 'ㄷㅅ', 'ㄷㅇ', 'ㄷㅈ', 'ㄷㅊ', 'ㄷㅌ', 'ㄷㅎ',
  'ㅁㄱ', 'ㅁㄴ', 'ㅁㄷ', 'ㅁㄹ', 'ㅁㅁ', 'ㅁㅂ', 'ㅁㅅ', 'ㅁㅇ', 'ㅁㅈ', 'ㅁㅊ', 'ㅁㅌ', 'ㅁㅍ', 'ㅁㅎ',
  'ㅂㄱ', 'ㅂㄴ', 'ㅂㄷ', 'ㅂㄹ', 'ㅂㅁ', 'ㅂㅂ', 'ㅂㅅ', 'ㅂㅇ', 'ㅂㅈ', 'ㅂㅊ', 'ㅂㅌ', 'ㅂㅍ', 'ㅂㅎ',
  'ㅅㄱ', 'ㅅㄴ', 'ㅅㄷ', 'ㅅㄹ', 'ㅅㅁ', 'ㅅㅂ', 'ㅅㅅ', 'ㅅㅇ', 'ㅅㅈ', 'ㅅㅊ', 'ㅅㅋ', 'ㅅㅌ', 'ㅅㅍ', 'ㅅㅎ',
  'ㅇㄱ', 'ㅇㄴ', 'ㅇㄷ', 'ㅇㄹ', 'ㅇㅁ', 'ㅇㅂ', 'ㅇㅅ', 'ㅇㅇ', 'ㅇㅈ', 'ㅇㅊ', 'ㅇㅋ', 'ㅇㅌ', 'ㅇㅍ', 'ㅇㅎ',
  'ㅈㄱ', 'ㅈㄴ', 'ㅈㄷ', 'ㅈㄹ', 'ㅈㅁ', 'ㅈㅂ', 'ㅈㅅ', 'ㅈㅇ', 'ㅈㅈ', 'ㅈㅊ', 'ㅈㅎ',
  'ㅊㄱ', 'ㅊㄷ', 'ㅊㄹ', 'ㅊㅁ', 'ㅊㅂ', 'ㅊㅅ', 'ㅊㅇ', 'ㅊㅈ', 'ㅊㅊ', 'ㅊㅎ',
  'ㅋㄷ', 'ㅋㄹ', 'ㅋㅍ',
  'ㅌㄱ', 'ㅌㄹ', 'ㅌㅅ', 'ㅌㅇ',
  'ㅍㄷ', 'ㅍㄹ', 'ㅍㅁ', 'ㅍㅅ', 'ㅍㅇ', 'ㅍㅈ',
  'ㅎㄱ', 'ㅎㄴ', 'ㅎㄷ', 'ㅎㄹ', 'ㅎㅁ', 'ㅎㅂ', 'ㅎㅅ', 'ㅎㅇ', 'ㅎㅈ', 'ㅎㅊ',
];

function generateRandomQuestions(count = 5) {
  const shuffled = [...VERIFIED_INITIALS_POOL];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled.slice(0, count).map((initials) => ({ initials }));
}

async function validateAnswer(answer, quizInitials) {
  const res = await fetch('/api/quiz/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer, quizInitials }),
  });

  if (!res.ok) {
    throw new Error(`Validate API Error: ${res.status}`);
  }

  const data = await res.json();
  return {
    valid: Boolean(data.valid),
    reason: data.reason || null,
  };
}

function GamePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { rows, cols } = location.state || { rows: 3, cols: 3 };
  const isRankingMode = true;

  const [quizConfig, setQuizConfig] = useState({
    quizKicker: '이벤트 모드',
    quizTitle: '초성퀴즈',
    quizInputPlaceholder: '정답 입력',
    quizSubmitText: '정답 확인',
    quizCheckingText: '확인 중',
    quizEmptyMessage: '정답을 입력해 주세요.',
    quizCheckingMessage: '정답을 확인 중입니다.',
    quizWrongMessage: '초성이 맞지 않습니다.',
    quizDictionaryMessage: '우리말샘 사전에서 찾을 수 없는 단어입니다.',
    quizErrorMessage: '정답 확인 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
    quizGiveUpText: '포기하기',
  });
  const [answer, setAnswer] = useState('');
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState(() => generateRandomQuestions(5));
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    fetch('/api/config')
      .then((res) => res.json())
      .then((data) => {
        setQuizConfig((prev) => ({ ...prev, ...data }));
      })
      .catch((err) => {
        console.error('Failed to load config:', err);
      });

    // 매 게임마다 무조건 랜덤 초성 5문제를 생성하여 출제
    fetch('/api/quiz/random-initials?count=5')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch random initials');
        return res.json();
      })
      .then((initialsList) => {
        if (Array.isArray(initialsList) && initialsList.length >= 5) {
          setQuestions(initialsList.map((initials) => ({ initials })));
        } else {
          setQuestions(generateRandomQuestions(5));
        }
      })
      .catch((err) => {
        console.warn('Using fallback random initials:', err);
        setQuestions(generateRandomQuestions(5));
      })
      .finally(() => {
        startTimeRef.current = Date.now();
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isLoading) return undefined;
    const timer = window.setInterval(() => {
      setElapsedTime((Date.now() - startTimeRef.current) / 1000);
    }, 100);
    return () => window.clearInterval(timer);
  }, [isLoading]);

  const finishGame = (finalAnswers) => {
    const clearTime = (Date.now() - startTimeRef.current) / 1000;
    navigate('/result', {
      state: {
        isCorrect: true,
        answer: finalAnswers.join(', '),
        rows,
        cols,
        isRankingMode,
        clearTime,
      },
    });
  };

  const checkAnswer = async () => {
    if (isChecking) return;

    if (!answer.trim()) {
      setMessage(quizConfig.quizEmptyMessage);
      return;
    }

    const quizInitials = questions[currentQuestion].initials;

    setIsChecking(true);
    setMessage(quizConfig.quizCheckingMessage);

    try {
      const validation = await validateAnswer(answer, quizInitials);

      if (!validation.valid) {
        if (validation.reason === 'dictionary') {
          setMessage(quizConfig.quizDictionaryMessage);
        } else if (validation.reason === 'error') {
          setMessage(quizConfig.quizErrorMessage);
        } else {
          setMessage(quizConfig.quizWrongMessage);
        }
        return;
      }

      const nextAnswers = [...answers, answer.trim()];
      if (currentQuestion === questions.length - 1) {
        finishGame(nextAnswers);
        return;
      }

      setAnswers(nextAnswers);
      setCurrentQuestion((current) => current + 1);
      setAnswer('');
      setMessage('');
    } catch (err) {
      console.error('Answer validation failed:', err);
      setMessage(quizConfig.quizErrorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    checkAnswer();
  };

  const handleKeyboardPress = (button) => {
    if (isLoading || isChecking) return;

    if (button === '{enter}') {
      checkAnswer();
      return;
    }

    setAnswer((current) => {
      const letters = Hangul.disassemble(current);

      if (button === '{bksp}') {
        letters.pop();
      } else if (button === '{space}') {
        letters.push(' ');
      } else {
        letters.push(button);
      }

      return Hangul.assemble(letters);
    });
  };

  const handleGiveUp = () => {
    navigate('/result', {
      state: {
        isCorrect: false,
        rows,
        cols,
        isRankingMode,
      },
    });
  };

  return (
    <div className="page-container game-page quiz-page">
      <div className="quiz-shell">
        <div className="quiz-header">
          <div>
            <p className="quiz-kicker">{isRankingMode ? `타임어택 ${elapsedTime.toFixed(1)}초` : quizConfig.quizKicker}</p>
            <h1 className="game-title quiz-title">{quizConfig.quizTitle}</h1>
          </div>
        </div>

        <div className="quiz-progress" aria-label="quiz progress">
          <div style={{ width: `${(currentQuestion / questions.length) * 100}%` }} />
        </div>

        <main className="quiz-card">
          <div className="quiz-initials" aria-label="초성">
            {isLoading ? '...' : questions[currentQuestion].initials}
          </div>

          <form className="quiz-form" onSubmit={handleSubmit}>
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              autoFocus
              placeholder={quizConfig.quizInputPlaceholder}
              className="quiz-input"
              disabled={isLoading || isChecking}
            />
            <button type="submit" className="action-button quiz-submit" disabled={isLoading || isChecking}>
              {isChecking ? quizConfig.quizCheckingText : quizConfig.quizSubmitText}
            </button>
          </form>

          {message && <p className="quiz-message">{message}</p>}
        </main>

        <div className="keyboard-container show">
          <Keyboard
            layout={{
              default: [
                'ㅂ ㅈ ㄷ ㄱ ㅅ ㅛ ㅕ ㅑ ㅐ ㅔ',
                'ㅁ ㄴ ㅇ ㄹ ㅎ ㅗ ㅓ ㅏ ㅣ',
                'ㅋ ㅌ ㅊ ㅍ ㅠ ㅜ ㅡ {bksp}',
                '{space} {enter}',
              ],
            }}
            display={{
              '{bksp}': '⌫',
              '{enter}': '확인',
              '{space}': '공백',
            }}
            onKeyPress={handleKeyboardPress}
          />
        </div>

        <div className="quiz-footer">
          <div className="quiz-solved-list">
            {questions.map((question, index) => (
              <span
                key={`${question.initials}-${index}`}
                className={index < currentQuestion ? 'is-solved' : index === currentQuestion ? 'is-current' : ''}
              >
                {index + 1}
              </span>
            ))}
          </div>
          <button type="button" onClick={handleGiveUp} className="secondary-button">
            {quizConfig.quizGiveUpText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GamePage;
